import { ControlSeverityMssp } from "../../../../utils/constants";

export const calculateTotalGaps = (selectedTool) => {
  if (!selectedTool) return 0;

  return (
    selectedTool?.controls[ControlSeverityMssp.HIGH]?.length +
    selectedTool?.controls[ControlSeverityMssp.MEDIUM]?.length +
    selectedTool?.controls[ControlSeverityMssp.LOW]?.length
  );
};

export const calculateTotalControls = (selectedTool, totalGaps) => {
  if (!selectedTool) return 0;
  return (
    totalGaps + selectedTool?.controls[ControlSeverityMssp.COMPLIANT]?.length
  );
};

export const calculateCoverageScore = (
  selectedTool,
  totalGaps,
  totalControls
) => {
  if (!selectedTool) return "0";

  const coverageScore =
    (selectedTool?.controls[ControlSeverityMssp.COMPLIANT]?.length /
      totalControls) *
    100;
  return coverageScore.toFixed(0);
};

export const filterTenantsBySeverity = (
  allTenants,
  activeSeverity,
  selectedTool
) => {
  if (!allTenants) return [];
  let filteredTenants = [];
  if (activeSeverity !== ControlSeverityMssp.NOT_DEPLOYED) {
    filteredTenants = allTenants.filter((tenant) =>
      selectedTool.tenants.includes(tenant.tenant_id)
    );
  }
  if (activeSeverity === ControlSeverityMssp.NOT_DEPLOYED) {
    filteredTenants = allTenants.filter((tenant) =>
      selectedTool.not_deployed_tenants.includes(tenant.tenant_id)
    );
  }

  return filteredTenants;
};

export const generateSeverityTabs = (selectedTool, colors) => {
  return [
    {
      label: "High",
      value: ControlSeverityMssp.HIGH,
      color: colors.ops_heatmap_red_fill,
      numberOfControls:
        selectedTool?.controls[ControlSeverityMssp.HIGH]?.length,
    },
    {
      label: "Medium",
      value: ControlSeverityMssp.MEDIUM,
      color: colors.ops_heatmap_orange_fill,
      numberOfControls:
        selectedTool?.controls[ControlSeverityMssp.MEDIUM]?.length,
    },
    {
      label: "Low",
      value: ControlSeverityMssp.LOW,
      color: colors.ops_heatmap_yellow_fill,
      numberOfControls: selectedTool?.controls[ControlSeverityMssp.LOW]?.length,
    },
    {
      label: "Compliant",
      value: ControlSeverityMssp.COMPLIANT,
      color: colors.ops_heatmap_green_fill,
      numberOfControls:
        selectedTool?.controls[ControlSeverityMssp.COMPLIANT]?.length,
    },
    {
      label: "Not Deployed",
      value: ControlSeverityMssp.NOT_DEPLOYED,
      color: colors.grey_400,
      disabled: selectedTool?.not_deployed_tenants?.length === 0,
      numberOfControls:
        selectedTool?.controls[ControlSeverityMssp.NOT_DEPLOYED]?.length *
        selectedTool?.not_deployed_tenants?.length,
    },
  ];
};

export const getNumberOfGapsBySeverity = (row, activeSeverity) => {
  const severityMap = {
    [ControlSeverityMssp.HIGH]: row?.[ControlSeverityMssp.HIGH],
    [ControlSeverityMssp.MEDIUM]: row?.[ControlSeverityMssp.MEDIUM],
    [ControlSeverityMssp.LOW]: row?.[ControlSeverityMssp.LOW],
    [ControlSeverityMssp.COMPLIANT]: row?.[ControlSeverityMssp.COMPLIANT],
    [ControlSeverityMssp.NOT_DEPLOYED]: row?.not_deployed,
  };

  return severityMap[activeSeverity] || 0;
};

export const getTotalGapsBySeverity = (row, activeSeverity) => {
  const totalGapsMap = {
    [ControlSeverityMssp.HIGH]: row?.total_high_controls,
    [ControlSeverityMssp.MEDIUM]: row?.total_medium_controls,
    [ControlSeverityMssp.LOW]: row?.total_low_controls,
    [ControlSeverityMssp.COMPLIANT]: 0,
    [ControlSeverityMssp.NOT_DEPLOYED]: 0,
  };

  return totalGapsMap[activeSeverity] || 0;
};

export const getSeverityColor = (severity, colors) => {
  const severityMap = {
    [ControlSeverityMssp.HIGH]: colors.ops_heatmap_red_fill,
    [ControlSeverityMssp.MEDIUM]: colors.ops_heatmap_orange_fill,
    [ControlSeverityMssp.LOW]: colors.ops_heatmap_yellow_fill,
    [ControlSeverityMssp.COMPLIANT]: colors.ops_heatmap_green_fill,
    [ControlSeverityMssp.NOT_DEPLOYED]: colors.grey_400,
  };

  return severityMap[severity] || colors.grey_400;
};

export const sortToolsByHighSeverity = (
  allTenantsControlsByTools,
  showNotDeployed
) => {
  if (!allTenantsControlsByTools) return [];

  return Object.values(allTenantsControlsByTools).sort((a, b) => {
    // Calculate the number of deployed controls for each tenant that are not not deployed
    const aAllDeployedControls =
      a.controls[ControlSeverityMssp.NOT_DEPLOYED]?.length *
      a.not_deployed_tenants?.length;

    // Calculate total controls for each tool
    const aTotalControls =
      a.controls[ControlSeverityMssp.HIGH]?.length +
      a.controls[ControlSeverityMssp.MEDIUM]?.length +
      a.controls[ControlSeverityMssp.LOW]?.length +
      a.controls[ControlSeverityMssp.COMPLIANT]?.length +
      (showNotDeployed ? aAllDeployedControls : 0);

    // Calculate the number of deployed controls for each tenant that are not not deployed
    const bAllDeployedControls =
      b.controls[ControlSeverityMssp.NOT_DEPLOYED]?.length *
      b.not_deployed_tenants?.length;

    const bTotalControls =
      b.controls[ControlSeverityMssp.HIGH]?.length +
      b.controls[ControlSeverityMssp.MEDIUM]?.length +
      b.controls[ControlSeverityMssp.LOW]?.length +
      b.controls[ControlSeverityMssp.COMPLIANT]?.length +
      (showNotDeployed ? bAllDeployedControls : 0);

    // Calculate high severity gaps percentage
    const aHighPercentage = aTotalControls
      ? (a.controls[ControlSeverityMssp.HIGH]?.length || 0) / aTotalControls
      : 0;

    const bHighPercentage = bTotalControls
      ? (b.controls[ControlSeverityMssp.HIGH]?.length || 0) / bTotalControls
      : 0;

    // Sort by percentage in descending order
    return bHighPercentage - aHighPercentage;
  });
};
